import React, { useState, useEffect } from "react";

// Style
import {
  TableWrap,
  TableContent,
  TableNames,
  AlfabetSort,
  DateSort,
  LeftContent,
  MidContent,
  RightContent,
  NoVideo,
  AddButton,
  SeeMore,
  Actions,
  ActionWrap,
  EmailList,
  EmailListWrap,
  Divider,
} from "../../style/Playlist";

// Componenst
import Modal from "../../components/modal/index";
import Empty from "../../components/empty/index";

// Images
import arrow from "../../images/down.svg";
import share from "../../images/ic_share.svg";
import share_blue from "../../images/share_blue.svg";
import sort from "../../images/ic_sort.svg";
import closeIcon from "../../images/ic_close.svg";

// Language
import { useTranslation } from "react-i18next";
import TextInput from "../inputs/TextInput";
import Button from "../button";
import { $apiV1 } from "../../config/api";
import { useLexicon } from "../../hooks/useLexicon";
import { trackPlaylistAction } from "../../tracking";

const LinkTable = ({ table, onShareSuccess }) => {
  const l = useLexicon();
  const { t, i18n } = useTranslation();
  const [openIndex, setOpenIndex] = useState(null);
  const [openShareVideo, setOpenShareVideo] = useState(false);
  const [openSuccessShareVideo, setOpenSuccessShareVideo] = useState(false);
  const [showAccessDeniedModal, setShowAccessDeniedModal] = useState(false);

  const [videoName, setVideoName] = useState("");
  const [currentEmail, setCurrentEmail] = useState("");
  const [emailList, setEmailList] = useState([]);
  
  // Email autocomplete state
  const [sharedEmails, setSharedEmails] = useState([]);
  const [filteredEmails, setFilteredEmails] = useState([]);
  const [showEmailDropdown, setShowEmailDropdown] = useState(false);

  // Sorting State
  const [sortOrder, setSortOrder] = useState("desc");
  const [sortBy, setSortBy] = useState("date");

  const sortData = (data) => {
    // Ensure data is an array
    if (!Array.isArray(data)) {
      console.error("Data is not an array:", data);
      return [];
    }

    return [...data].sort((a, b) => {
      if (sortBy === "title") {
        return sortOrder === "asc"
          ? a.playlist_title.localeCompare(b.playlist_title)
          : b.playlist_title.localeCompare(a.playlist_title);
      } else if (sortBy === "date") {
        return sortOrder === "asc"
          ? new Date(a.date) - new Date(b.date)
          : new Date(b.date) - new Date(a.date);
      }
      return 0;
    });
  };

  const sortedTable = sortData(table?.results || []);

  // Fetch shared emails on component mount
  useEffect(() => {
    fetchSharedEmails();
  }, []);

  const fetchSharedEmails = async () => {
    try {
      const response = await $apiV1.get('/users/shared_emails');
      setSharedEmails(response.data || []);
      setFilteredEmails(response.data || []);
    } catch (error) {
      console.error('Error fetching shared emails:', error);
    }
  };

  // Functions for handling sort
  const handleSortByTitle = () => {
    setSortBy("title");
    setSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
  };

  const handleSortByDate = () => {
    setSortBy("date");
    setSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
  };

  const handleShareVideo = (item) => {
    // Check if playlist is blocked
    if (item?.is_blocked === true) {
      setShowAccessDeniedModal(true);
      return;
    }
    setVideoName(item);
    setOpenShareVideo(!openShareVideo);
    setEmailList([]);
    setCurrentEmail("");
  };

  const handleEmailList = (newValue) => {
    setCurrentEmail(newValue);
    
    if (newValue.trim()) {
      const filtered = sharedEmails.filter(email => 
        typeof email === 'string' && email.toLowerCase().includes(newValue.toLowerCase())
      );

      setFilteredEmails(filtered);
      setShowEmailDropdown(filtered.length > 0);
    } else {
      setFilteredEmails(sharedEmails);
      setShowEmailDropdown(true);
    }
  };

  const handleAddEmail = () => {
    setEmailList([...emailList, currentEmail]);
    setCurrentEmail("");
  };

  const handleRemoveEmail = (index) => {
    setEmailList(emailList.filter((_, i) => i !== index));
  };

  useEffect(() => {}, [emailList]);

  const handleSharePlayers = async (videoName) => {
    try {
      await $apiV1.post(`/users/share/`, {
        email: emailList,
        playlist: videoName.playlist,
      });
      trackPlaylistAction({
        action: "share",
        entityId: videoName?.playlist,
        entityLabel: videoName?.playlist_title || videoName?.title || null,
        metadata: {
          recipients_count: emailList.length,
          shared_with: emailList,
        },
        sourceSurface: "playlist",
      });
      setOpenSuccessShareVideo(true);
      setOpenShareVideo(false);
      await fetchSharedEmails();
      if (typeof onShareSuccess === "function") {
        await onShareSuccess();
      }
    } catch (error) {
      console.error("Error fetching match data:", error);
    }
  };

  const handleEmailSelect = (email) => {
    setCurrentEmail(email);
    setShowEmailDropdown(false);
    setFilteredEmails([]);
  };

  const modal = (
    <>
      {/* Share folder */}
      <Modal isOpen={openShareVideo} setIsOpen={setOpenShareVideo} closeIc>
        <h1>{l(965)}</h1>
        <p>
          Share {videoName?.title} via next social media, or just copy link.
        </p>
        
        <div style={{ position: 'relative' }}>
          <TextInput
            type="email"
            value={currentEmail}
            onChange={handleEmailList}
            placeholder={l(966)}
            onFocus={() => {
              setShowEmailDropdown(true);
            }}
            onBlur={() => {
              // Delay hiding dropdown to allow for clicks
              setTimeout(() => setShowEmailDropdown(false), 200);
            }}
          />
          {showEmailDropdown && filteredEmails.length > 0 && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              backgroundColor: 'black',
              color: 'white',
              textAlign: 'left',
              border: '1px solid #ddd',
              borderRadius: '4px',
              maxHeight: '200px',
              overflowY: 'auto',
              zIndex: 1000,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              {filteredEmails.slice(0, 3).map((email, index) => (
                <div
                  key={index}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    borderBottom: index < filteredEmails.length - 1 ? '1px solid #eee' : 'none',
                    fontSize: '14px',
                    fontFamily: 'SatoshiRegular'
                  }}
                  onMouseDown={() => handleEmailSelect(email)}
                >
                  {email}
                </div>
              ))}
            </div>
          )}
        </div>

        <Button
          title={l(967)}
          size={"full"}
          color={"secondary"}
          onClick={() => handleAddEmail(currentEmail)}
          style={{ marginTop: "30px" }}
        />

        {emailList.length > 0 && (
          <EmailList>
            {emailList.map((email, index) => (
              <EmailListWrap key={index}>
                {email}{" "}
                <img
                  src={closeIcon}
                  alt={closeIcon}
                  onClick={() => handleRemoveEmail(index)}
                />
              </EmailListWrap>
            ))}
            <Button
              title={l(968)}
              size={"full"}
              color={"secondary"}
              onClick={() => handleSharePlayers(videoName)}
              style={{ marginTop: "30px" }}
            />
          </EmailList>
        )}
      </Modal>
      {/* End Share folder */}

      {/* Success Copy link */}
      <Modal
        isOpen={openSuccessShareVideo}
        setIsOpen={setOpenSuccessShareVideo}
        successIc
      >
        <h1>{t("playlist.successCopyTitle")}</h1>
        <p style={{ marginBottom: "40px" }}>{t("playlist.successCopyDesc")}</p>

        <Button
          title={t("playlist.successCopyButton")}
          size={"full"}
          onClick={() => setOpenSuccessShareVideo(!openSuccessShareVideo)}
        />
      </Modal>
      {/* Success Copy link */}
      
      {/* Access Denied Modal */}
      <Modal isOpen={showAccessDeniedModal} setIsOpen={setShowAccessDeniedModal} closeIc>
        <h1>Access Denied</h1>
        <p>Access denied. Please contact your account manager or remove videos you do not have permission to access.</p>
        <br></br>
        <Button
          title="Close"
          size={"full"}
          onClick={() => setShowAccessDeniedModal(false)}
        />
      </Modal>
    </>
  );

  const toggleOpen = (index) => {
    setOpenIndex((prevIndex) => (prevIndex === index ? null : index));
  };

  return (
    <>
      <TableNames>
        <AlfabetSort onClick={handleSortByTitle} style={{ cursor: "pointer" }}>
          <h6>{l(962)}</h6>
          <img src={sort} alt="sort" />
        </AlfabetSort>
        <Divider style={{ marginRight: "35px" }}></Divider>
        <DateSort onClick={handleSortByDate} style={{ cursor: "pointer" }}>
          <h6>{l(132)}</h6>
          <img src={sort} alt="sort" />
        </DateSort>
      </TableNames>
      {sortedTable.length > 0 ? (
        <TableWrap style={{ height: "fit-content" }}>
          {sortedTable.map((item, index) => {
            return (
              <>
                <TableContent key={index}>
                  <LeftContent
                    style={{ color: "#fff" }}
                    onClick={() => toggleOpen(index)}
                  >
                    <img src={arrow} alt={arrow} />{" "}
                    <h3>{item.playlist_title}</h3>
                  </LeftContent>
                  <MidContent>
                    <h5>{item.views_count}</h5>
                  </MidContent>
                  <MidContent>
                    <h5>{item.date?.split("T")?.[0]}</h5>
                  </MidContent>
                  <RightContent>
                    <>
                      <img
                        src={localStorage.getItem("theme") === "light" ? share_blue : share}
                        alt={share}
                        onClick={() => handleShareVideo(item)}
                        // onClick={() => setOpenShareVideo(!openShareVideo)}
                      />
                    </>
                  </RightContent>
                </TableContent>

                {openIndex === index && (
                  <>
                    {item.share_with.length > 0 ? (
                      item.share_with.map((item, index) => {
                        return (
                          <TableContent>
                            <LeftContent style={{ color: "#fff" }}>
                              <h3>{item}</h3>
                            </LeftContent>
                          </TableContent>
                        );
                      })
                    ) : (
                      <NoVideo>
                        <h6>{l(1057)}</h6>
                      </NoVideo>
                    )}
                  </>
                )}
              </>
            );
          })}
        </TableWrap>
      ) : (
        <Empty
          title={l(134)}
          text={l(1058)}
          button1={t("playlist.emptyPlaylistButton")}
        />
      )}
      {modal}
    </>
  );
};

export default LinkTable;
